// Platforms — TradeFlow design (light, mobile-first, Apple fintech)
// Shows trading platforms gated by user's subscription. Uses TfCard primitive.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { APP_ID, type Platform, type Subscription } from '@funderpro/shared';
import { getToken, API_BASE } from '../../lib/auth';
import { TfCard } from '../../components/TfPrimitives';
import { Loading, ErrorBox } from './UIShell';
import { BrandLogo } from '../../components/Brand';
import { TfStatusDot } from '../../components/TfPrimitives';

export function PlatformsPage() {
  const [platforms, setPlatforms] = useState<Platform[] | null>(null);
  const [subs, setSubs] = useState<Subscription[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const t = getToken();
    Promise.all([
      fetch(`${API_BASE}/api/apps/${APP_ID}/entities/Platform`).then(r => r.json()),
      fetch(`${API_BASE}/api/apps/${APP_ID}/entities/Subscription`, { headers: { Authorization: `Bearer ${t}` } }).then(r => r.json()).catch(() => []),
    ]).then(([p, s]) => {
      setPlatforms(Array.isArray(p) ? p : []);
      setSubs(Array.isArray(s) ? s : []);
    }).catch(e => setErr(String(e?.message || e)));
  }, []);

  if (err) return <ErrorBox>{err}</ErrorBox>;
  if (!platforms || !subs) return <Loading />;

  // Platform is "available" if user has at least one active subscription of the right kind
  const hasChallenge = subs.some(s => s.kind === 'challenge' && s.status === 'active');
  const hasMarketData = subs.some(s => s.kind === 'market_data' && s.status === 'active');
  const isAvailable = (p: Platform) => {
    if (p.requires_subscription === 'challenge') return hasChallenge;
    if (p.requires_subscription === 'market_data') return hasMarketData;
    if (p.requires_subscription === 'both') return hasChallenge && hasMarketData;
    return true;
  };

  return (
    <div className="space-y-4 p-4">
      <h1 className="px-1 text-[34px] font-bold leading-[1.1] tracking-tight text-tf-text-1">Platforms</h1>
      <p className="px-1 text-[15px] text-tf-text-3">Trading platforms available based on your subscriptions.</p>

      <TfCard className="p-5">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-[14px] bg-tf-blue-tint text-tf-blue text-[24px]">💻</div>
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-wider text-tf-text-3">Subscriptions</div>
            <div className="mt-0.5 flex items-center gap-2 text-[15px] font-semibold text-tf-text-1">
              {subs.length > 0 ? (
                <>
                  <TfStatusDot variant="success" />
                  {subs.length} active subscription{subs.length === 1 ? '' : 's'}
                </>
              ) : (
                <>
                  <TfStatusDot variant="warning" />
                  No subscriptions yet
                </>
              )}
            </div>
          </div>
        </div>
        {subs.length === 0 && (
          <p className="mt-3 text-[13px] text-tf-text-2">
            Buy a challenge to get instant access to all platforms.
          </p>
        )}
      </TfCard>

      {platforms.length > 0 && (
        <div className="grid gap-3 md:grid-cols-2">
          {platforms.map(p => {
            const available = isAvailable(p);
            return (
              <TfCard key={p.id} className={['p-4', !available ? 'opacity-60' : ''].join(' ')} testId={`platform-${p.id}`}>
                <div className="flex items-start gap-3">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-tf-surface-1 border border-tf-border p-1.5">
                    <BrandLogo name={p.name} size={36} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="text-[15px] font-bold text-tf-text-1">{p.name}</h3>
                      <span className={['rounded-full px-2 py-0.5 text-[10px] font-bold uppercase',
                        available ? 'bg-tf-green-tint text-tf-profit' : 'bg-tf-surface-3 text-tf-text-3'
                      ].join(' ')}>
                        {available ? 'Available' : 'Locked'}
                      </span>
                    </div>
                    <p className="mt-1 text-[13px] text-tf-text-3">{p.description}</p>
                    <p className="mt-2 text-[11px] text-tf-text-3">Requires: {p.requires_subscription}</p>
                  </div>
                </div>
              </TfCard>
            );
          })}
        </div>
      )}

      {subs.length === 0 && (
        <div className="flex justify-center pt-2">
          <Link
            to="/portal/platforms"
            className="flex h-12 items-center justify-center rounded-full bg-tf-text-1 px-7 text-[15px] font-semibold text-white active:scale-[0.98]"
            style={{ boxShadow: '0 8px 24px -6px rgba(28,28,30,0.55)' }}
          >
            Buy a Challenge
          </Link>
        </div>
      )}
    </div>
  );
}
